import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { bg, line, tx, font, accent, space, radius } from '../theme';
import { fetchClubs, dbConfigured } from '../game/gameApi';
import ClubCrest from '../game/ClubCrest';
import { createSave, listSaves, unlockAchievement, SAVE_LIMIT } from './accountApi';

const TIER_NAMES = { 1: 'Premier League', 2: 'Championship', 3: 'League One', 4: 'League Two' };

function Screen({ children }) {
  return (
    <div style={{
      minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: tx.muted, fontFamily: font.display, fontSize: 12,
      letterSpacing: '0.2em', textTransform: 'uppercase',
    }}>{children}</div>
  );
}

export default function NewGame() {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [status, setStatus] = useState('loading');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!dbConfigured) { setStatus('error'); return undefined; }
    (async () => {
      const [c, s] = await Promise.all([fetchClubs(), listSaves()]);
      if (!alive) return;
      if (c.error) { setStatus('error'); return; }
      if (!c.data || c.data.length === 0) { setStatus('empty'); return; }
      if (!s.error && (s.data || []).length >= SAVE_LIMIT) { setClubs(c.data); setStatus('full'); return; }
      setClubs(c.data);
      setStatus('ready');
    })();
    return () => { alive = false; };
  }, []);

  async function pick(clubId) {
    if (creating) return;
    setCreating(true);
    const club = clubs.find((c) => c.id === clubId);
    const { data, error } = await createSave(clubId, club && club.name);
    if (error || !data) { setCreating(false); setStatus(error && error.message && error.message.includes('save_limit') ? 'full' : 'savefail'); return; }
    unlockAchievement('first_save');
    navigate(`/game/${data.id}`, { replace: true });
  }

  if (status === 'loading') return <Screen>Loading clubs</Screen>;
  if (status === 'error') return <Screen>Couldn&rsquo;t reach the world database</Screen>;
  if (status === 'savefail') return <Screen>Couldn&rsquo;t start the save. Try again</Screen>;
  if (status === 'empty') return <Screen>No clubs found</Screen>;
  if (status === 'full') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', padding: space.s6, textAlign: 'center' }}>
        <div style={{
          fontFamily: font.display, fontSize: 11, fontWeight: 700, letterSpacing: '0.24em',
          textTransform: 'uppercase', color: accent.amber, marginBottom: space.s3,
        }}>Save slots full</div>
        <h1 style={{ fontFamily: font.display, fontSize: 26, fontWeight: 700, margin: 0 }}>
          You&rsquo;ve used all {SAVE_LIMIT} save slots.
        </h1>
        <p style={{ color: tx.secondary, margin: `${space.s3}px 0 ${space.s5}px` }}>
          Free up a slot by deleting a save, then come back to take a new club.
        </p>
        <Link to="/hub/saves" style={{
          color: accent.lime, fontFamily: font.display, fontWeight: 600, textDecoration: 'none', fontSize: 15,
        }}>Manage saves &rsaquo;</Link>
      </div>
    );
  }

  const tiers = [1, 2, 3, 4].filter((t) => clubs.some((c) => c.tier === t));

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: space.s6, opacity: creating ? 0.6 : 1 }}>
      <div style={{ marginBottom: space.s6 }}>
        <Link to="/hub" style={{ color: tx.muted, textDecoration: 'none', fontFamily: font.data, fontSize: 13 }}>
          &lsaquo; Back to hub
        </Link>
        <div style={{
          fontFamily: font.display, fontSize: 11, fontWeight: 700, letterSpacing: '0.24em',
          textTransform: 'uppercase', color: accent.lime, margin: `${space.s4}px 0 ${space.s2}px`,
        }}>
          New appointment
        </div>
        <h1 style={{ fontFamily: font.display, fontSize: 30, fontWeight: 700, margin: 0 }}>Take a club.</h1>
        <p style={{ color: tx.secondary, marginTop: space.s3, maxWidth: '58ch' }}>
          Choose the club for this save. Each save is its own appointment, so you can run several at once.
        </p>
      </div>

      {tiers.map((tier) => {
        const group = clubs.filter((c) => c.tier === tier);
        return (
          <section key={tier} style={{ marginBottom: space.s6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: space.s3, marginBottom: space.s4 }}>
              <span style={{
                fontFamily: font.display, fontSize: 12, fontWeight: 700, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: tx.muted,
              }}>{TIER_NAMES[tier] || `Tier ${tier}`}</span>
              <span style={{ flex: 1, height: 1, background: line.DEFAULT }} />
              <span style={{ fontFamily: font.data, fontSize: 11, color: tx.faint }}>{group.length} clubs</span>
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: space.s3,
            }}>
              {group.map((club) => (
                <button
                  key={club.id}
                  disabled={creating}
                  onClick={() => pick(club.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: space.s3, textAlign: 'left',
                    cursor: creating ? 'default' : 'pointer', background: bg.surface,
                    border: `1px solid ${line.DEFAULT}`, borderRadius: radius.lg, padding: space.s3,
                    transition: 'border-color .14s, background .14s',
                  }}
                  onMouseEnter={(e) => { if (!creating) { e.currentTarget.style.borderColor = accent.lime; e.currentTarget.style.background = bg.raised; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = line.DEFAULT; e.currentTarget.style.background = bg.surface; }}
                >
                  <ClubCrest club={club} size={40} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{
                      display: 'block', fontFamily: font.display, fontWeight: 600, fontSize: 14,
                      color: tx.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{club.name}</span>
                    <span style={{ display: 'block', fontFamily: font.data, fontSize: 11, color: tx.faint, marginTop: 2 }}>
                      {club.town}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
